import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'

type ActionConfig = {
  label: string
  to?: string
  onClick?: () => void
}

type NotFoundViewProps = {
  /** Optional wrapper className (e.g. for full-page vs inline) */
  className?: string
  /** Show the large "404" heading */
  showCode?: boolean
  /** Title below the code (e.g. "Oops! Page Not Found!" or "Compliance not found") */
  title?: string
  /** Optional description text or ReactNode */
  description?: ReactNode
  /** Primary action button (link or onClick) */
  primaryAction?: ActionConfig
  /** Optional secondary action */
  secondaryAction?: ActionConfig
}

export function NotFoundView({
  className,
  showCode = true,
  title = 'Oops! Page Not Found!',
  description = "It seems like the page you're looking for does not exist or might have been removed.",
  primaryAction,
  secondaryAction,
}: NotFoundViewProps) {
  const renderAction = (action: ActionConfig, variant: 'default' | 'outline' = 'outline') => {
    if (action.to != null) {
      return (
        <Button asChild variant={variant}>
          <Link to={action.to}>{action.label}</Link>
        </Button>
      )
    }
    return (
      <Button variant={variant} onClick={action.onClick}>
        {action.label}
      </Button>
    )
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center justify-center gap-2 text-center',
        className
      )}
    >
      {showCode && (
        <h1 className='text-[7rem] leading-tight font-bold'>404</h1>
      )}
      {title && <span className='font-medium'>{title}</span>}
      {description != null && (
        <p className='text-muted-foreground'>{description}</p>
      )}
      {(primaryAction != null || secondaryAction != null) && (
        <div className='mt-6 flex gap-4'>
          {secondaryAction != null && renderAction(secondaryAction, 'outline')}
          {primaryAction != null && renderAction(primaryAction, secondaryAction != null ? 'default' : 'outline')}
        </div>
      )}
    </div>
  )
}
